import UserGamification from "./UserGamification";
import useAdminAccess from "hooks/useAdminAccess";
import AdminPermission from "components/admin/AdminPermission";
import {
  httpAppendUserInternalNote,
  httpClearUserPriceAlert,
  httpEscalateUserKyc,
  httpRejectUserKyc,
  httpUserAccountStatements,
  httpUserBeneficiaries,
  httpUserInternalNotes,
  httpUserKyc,
  httpUserPriceAlerts,
  httpUserSavedAddresses,
} from "api/admin/user";
import LinkedDevices from "components/admin/devices/LinkedDevices";
import TransactionItem from "components/ui/TransactionItem";
import { DateTime } from "luxon";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Link from "components/admin/AdminLink";
import { adminRoutes } from "routes/routes";
import { showDanger, showSuccess } from "utilities/misc";
import { post } from "utilities/network";
import UserSecurity from "./UserSecurity";

interface UserInfoProps {
  user: any;
  toggle?: (user: any) => void;
  refetch?: () => void | Promise<unknown>;
}

type UserProfileTab =
  | "gamification"
  | "overview"
  | "kyc"
  | "alerts"
  | "beneficiaries"
  | "statements"
  | "notes";

const tabs: Array<{
  label: string;
  value: UserProfileTab;
  permissions: string[];
}> = [
  {
    label: "Gamification",
    value: "gamification",
    permissions: ["gamification.eloTiers.view"],
  },
  { label: "Overview", value: "overview", permissions: ["users.profile.view"] },
  { label: "KYC", value: "kyc", permissions: ["users.kyc.view"] },
  { label: "Alerts", value: "alerts", permissions: ["users.priceAlerts.view"] },
  {
    label: "Beneficiaries",
    value: "beneficiaries",
    permissions: ["users.beneficiaries.view", "users.savedAddresses.view"],
  },
  {
    label: "Statements",
    value: "statements",
    permissions: ["users.accountStatements.view"],
  },
  { label: "Notes", value: "notes", permissions: ["users.internalNotes.view"] },
];

const countryLabels: Record<string, string> = {
  NG: "Nigeria",
  GH: "Ghana",
  KE: "Kenya",
};

const countryLabel = (country?: string | null) =>
  country ? countryLabels[country] || country : "-";

const dateLabel = (date?: string | Date | null) =>
  date ? DateTime.fromISO(String(date)).toFormat("DD t") : "-";

const Section: React.FC<{ children: React.ReactNode; title: string }> = ({
  children,
  title,
}) => (
  <section className="p-4 mb-6 bg-white border border-orange-100 rounded">
    <h5 className="mb-4 text-sm font-semibold text-black">{title}</h5>
    {children}
  </section>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <p className="py-4 text-sm text-center text-gray-500">{label}</p>
);

const DataRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex justify-between gap-4 mb-3 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="max-w-[58%] break-words text-right text-gray-800">
      {value || "-"}
    </span>
  </div>
);

const UserInfo: React.FC<UserInfoProps> = ({ user, toggle, refetch }) => {
  const queryClient = useQueryClient();
  const { can, canAny } = useAdminAccess();
  const visibleTabs = tabs.filter((tab) => canAny(tab.permissions));
  const [selectedTab, setSelectedTab] = useState<UserProfileTab>("overview");
  const [note, setNote] = useState("");
  const [kycReason, setKycReason] = useState("");
  const [alertReasons, setAlertReasons] = useState<Record<number, string>>({});
  const userID = user?.id;
  const activeTab =
    visibleTabs.find((tab) => tab.value === selectedTab)?.value ??
    visibleTabs[0]?.value;

  const kycQuery = useQuery(
    ["adminUserKyc", userID],
    () => httpUserKyc(userID),
    {
      enabled: !!userID && activeTab === "kyc" && can("users.kyc.view"),
    },
  );
  const alertsQuery = useQuery(
    ["adminUserPriceAlerts", userID],
    () => httpUserPriceAlerts(userID),
    {
      enabled:
        !!userID && activeTab === "alerts" && can("users.priceAlerts.view"),
    },
  );
  const beneficiariesQuery = useQuery(
    ["adminUserBeneficiaries", userID],
    () => httpUserBeneficiaries(userID),
    {
      enabled:
        !!userID &&
        activeTab === "beneficiaries" &&
        can("users.beneficiaries.view"),
    },
  );
  const savedAddressesQuery = useQuery(
    ["adminUserSavedAddresses", userID],
    () => httpUserSavedAddresses(userID),
    {
      enabled:
        !!userID &&
        activeTab === "beneficiaries" &&
        can("users.savedAddresses.view"),
    },
  );
  const statementsQuery = useQuery(
    ["adminUserAccountStatements", userID],
    () => httpUserAccountStatements(userID),
    {
      enabled:
        !!userID &&
        activeTab === "statements" &&
        can("users.accountStatements.view"),
    },
  );
  const notesQuery = useQuery(
    ["adminUserInternalNotes", userID],
    () => httpUserInternalNotes(userID),
    {
      enabled:
        !!userID && activeTab === "notes" && can("users.internalNotes.view"),
    },
  );

  const modifyUser = async (action: string) => {
    if (!can("users.profile.edit")) return;
    post(`admin/users/modifyUser/${userID}`, { action })
      .then(async () => {
        showSuccess("user modified successfully");
        toggle?.(user);
        refetch?.();
      })
      .catch(() => {
        showDanger("something went wrong");
      });
  };

  const kycActionMutation = useMutation(
    ({ action, reason }: { action: "reject" | "escalate"; reason: string }) =>
      action === "reject"
        ? httpRejectUserKyc(userID, reason)
        : httpEscalateUserKyc(userID, reason),
    {
      onSuccess: () => {
        showSuccess("KYC review updated");
        setKycReason("");
        queryClient.invalidateQueries(["adminUserKyc", userID]);
        refetch?.();
      },
    },
  );

  const clearAlertMutation = useMutation(
    ({ alertID, reason }: { alertID: number; reason: string }) =>
      httpClearUserPriceAlert(userID, alertID, reason),
    {
      onSuccess: () => {
        showSuccess("Price alert cleared");
        queryClient.invalidateQueries(["adminUserPriceAlerts", userID]);
      },
    },
  );

  const appendNoteMutation = useMutation(
    (nextNote: string) => httpAppendUserInternalNote(userID, nextNote),
    {
      onSuccess: () => {
        showSuccess("Internal note added");
        setNote("");
        queryClient.invalidateQueries(["adminUserInternalNotes", userID]);
      },
    },
  );

  const runKycAction = (action: "reject" | "escalate") => {
    if (!can("users.kyc.approve")) return;
    const reason = kycReason.trim();
    if (!reason) return showDanger("reason is required");
    kycActionMutation.mutate({ action, reason });
  };

  const clearAlert = (alertID: number) => {
    if (!can("users.priceAlerts.delete")) return;
    const reason = alertReasons[alertID]?.trim() || "";
    if (!reason) return showDanger("reason is required");
    clearAlertMutation.mutate({ alertID, reason });
  };

  const kyc = kycQuery.data?.kyc;
  const kycDocuments = kyc?.documents ? Object.entries(kyc.documents) : [];
  const fieldStatuses = kyc?.fieldStatuses || {};
  const priceAlerts = alertsQuery.data?.priceAlerts || [];
  const beneficiaries = beneficiariesQuery.data?.beneficiaries || [];
  const savedAddresses = savedAddressesQuery.data?.savedAddresses || [];
  const statements = statementsQuery.data?.accountStatements || [];
  const internalNotes = notesQuery.data?.internalNotes || [];

  return (
    <div className="w-5/12 px-6 py-8 rounded-lg bg-orange-50">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h4 className="mb-1">@{user?.userName}</h4>
          <p className="text-xs text-gray-500">
            {countryLabel(user?.country)} · Tier {user?.level ?? 0}
          </p>
        </div>
        <button
          className="px-3 py-1 text-xs text-white bg-black rounded"
          onClick={() => toggle?.(user)}
          type="button"
        >
          Close
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {visibleTabs.map((tab) => (
          <button
            className={`rounded px-3 py-1 text-xs ${
              activeTab === tab.value
                ? "bg-black text-white"
                : "bg-white text-gray-700"
            }`}
            key={tab.value}
            onClick={() => setSelectedTab(tab.value)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "gamification" && (
        <UserGamification key={userID} userID={Number(userID)} />
      )}
      {activeTab === "overview" && (
        <>
          <Section title="Profile">
            <TransactionItem title="Username" value={`@${user?.userName}`} />
            <TransactionItem
              title="Registered full Name"
              value={`${user?.firstName || ""} ${user?.lastName || ""}`}
            />
            <TransactionItem title="e-mail" value={user?.email} />
            <TransactionItem title="Phone Number" value={user?.phoneNumber} />
            <TransactionItem
              title="Country"
              value={countryLabel(user?.country)}
            />
            <TransactionItem
              title="Verification Level"
              value={`Level ${user?.level}`}
            />
            <TransactionItem
              title="Date registered"
              value={
                user?.regDate
                  ? DateTime.fromISO(user.regDate).toFormat("D")
                  : "-"
              }
            />
          </Section>

          <AdminPermission permission="users.profile.edit">
            <Section title="Controls">
              <TransactionItem
                title="Withdrawal Status"
                value=""
                sub={
                  <button
                    className="mt-2 rounded-full bg-black px-4 py-0.5 text-white"
                    onClick={() => modifyUser("withdraw")}
                    type="button"
                  >
                    {`${user?.canWithdraw ? "Disable" : "Enable"} Withdrawal`}
                  </button>
                }
              />
              <TransactionItem
                title="Deposit Status"
                value=""
                sub={
                  <button
                    className="mt-2 rounded-full bg-black px-4 py-0.5 text-white"
                    onClick={() => modifyUser("deposit")}
                    type="button"
                  >
                    {`${user?.canDeposit ? "Disable" : "Enable"} Deposit`}
                  </button>
                }
              />
              <TransactionItem
                title="User Status"
                value=""
                sub={
                  <button
                    className="mt-2 rounded-full bg-black px-4 py-0.5 text-white"
                    onClick={() => modifyUser("ban")}
                    type="button"
                  >
                    {`${user?.level < 1 ? "Unban" : "Ban"} User`}
                  </button>
                }
              />
            </Section>
          </AdminPermission>

          <div className="flex flex-wrap gap-3">
            <LinkButton to={`${adminRoutes.balances}/user/${userID}`}>
              Balances
            </LinkButton>
            <LinkButton to={`${adminRoutes.transactions}/user/${userID}`}>
              Transactions
            </LinkButton>
            <LinkButton to={`${adminRoutes.analytics}/user/${userID}`}>
              Analytics
            </LinkButton>
            <LinkButton to={`${adminRoutes.explorer}/user/${userID}`}>
              Addresses Explorer
            </LinkButton>
            <LinkButton to={`/virtual-cards/${userID}`}>
              Virtual Cards
            </LinkButton>
          </div>
        </>
      )}

      {activeTab === "kyc" && (
        <Section title="KYC and Documents">
          <div className="flex flex-wrap gap-3 mb-5">
            <LinkButton to={`${adminRoutes.eddReviews}/${userID}`}>
              Review Tier 3 · EDD
            </LinkButton>
            <LinkButton to={`${adminRoutes.kybReviews}/${userID}`}>
              Review Tier 4 · KYB
            </LinkButton>
          </div>
          {kycQuery.isLoading ? (
            <EmptyState label="Loading KYC profile..." />
          ) : (
            <>
              <DataRow
                label="Current tier"
                value={`Tier ${kycQuery.data?.tier ?? user?.level ?? 0}`}
              />
              <DataRow
                label="Current status"
                value={kyc?.currentStatus || "No submission"}
              />
              <DataRow
                label="Admin review"
                value={kyc?.adminReviewStatus || "-"}
              />
              <DataRow
                label="MetaMap status"
                value={kyc?.metamapStatus || "-"}
              />
              <DataRow
                label="Last action"
                value={dateLabel(kycQuery.data?.lastKycActionAt)}
              />

              <div className="mt-5">
                <h6 className="mb-2 text-xs font-semibold text-gray-600">
                  Field Statuses
                </h6>
                {Object.keys(fieldStatuses).length ? (
                  Object.entries(fieldStatuses).map(([key, value]) => (
                    <DataRow key={key} label={key} value={String(value)} />
                  ))
                ) : (
                  <EmptyState label="No field statuses available" />
                )}
              </div>

              <div className="mt-5">
                <h6 className="mb-2 text-xs font-semibold text-gray-600">
                  Documents
                </h6>
                {kycDocuments.length ? (
                  <div className="grid grid-cols-2 gap-3">
                    {kycDocuments.map(([key, document]: [string, any]) => (
                      <a
                        className="p-2 text-xs text-orange-900 underline border border-orange-100 rounded bg-orange-50"
                        href={document?.url || document}
                        key={key}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {document?.label || key}
                      </a>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="No document previews available" />
                )}
              </div>

              <AdminPermission permission="users.kyc.approve">
                <div className="flex gap-3 mt-5">
                  <textarea
                    className="min-h-[80px] flex-1 rounded border border-orange-100 p-3 text-sm"
                    onChange={(event) => setKycReason(event.target.value)}
                    placeholder="Reason required for KYC action"
                    value={kycReason}
                  />
                </div>
                <div className="flex gap-3 mt-3">
                  <button
                    className="px-4 py-2 text-sm text-white bg-red-600 rounded disabled:opacity-60"
                    disabled={
                      !kyc || !kycReason.trim() || kycActionMutation.isLoading
                    }
                    onClick={() => runKycAction("reject")}
                    type="button"
                  >
                    Reject
                  </button>
                  <button
                    className="px-4 py-2 text-sm text-white bg-black rounded disabled:opacity-60"
                    disabled={
                      !kyc || !kycReason.trim() || kycActionMutation.isLoading
                    }
                    onClick={() => runKycAction("escalate")}
                    type="button"
                  >
                    Escalate
                  </button>
                </div>
              </AdminPermission>
            </>
          )}
        </Section>
      )}

      {activeTab === "alerts" && (
        <Section title="Active Price Alerts">
          {priceAlerts.length ? (
            priceAlerts.map((alert: any) => (
              <div
                className="p-3 mb-3 border border-orange-100 rounded"
                key={alert.id}
              >
                <DataRow label="Coin" value={alert.symbol?.toUpperCase()} />
                <DataRow label="Target price" value={alert.target_price} />
                <DataRow label="Direction" value={alert.direction} />
                <DataRow label="Date set" value={dateLabel(alert.created_at)} />
                <AdminPermission permission="users.priceAlerts.delete">
                  <textarea
                    className="mb-2 min-h-[70px] w-full rounded border border-orange-100 p-2 text-xs"
                    onChange={(event) =>
                      setAlertReasons((current) => ({
                        ...current,
                        [alert.id]: event.target.value,
                      }))
                    }
                    placeholder="Reason required to clear alert"
                    value={alertReasons[alert.id] || ""}
                  />
                  <button
                    className="px-3 py-1 text-xs text-white bg-black rounded disabled:opacity-60"
                    disabled={
                      !alertReasons[alert.id]?.trim() ||
                      clearAlertMutation.isLoading
                    }
                    onClick={() => clearAlert(alert.id)}
                    type="button"
                  >
                    Clear Alert
                  </button>
                </AdminPermission>
              </div>
            ))
          ) : (
            <EmptyState label="No active price alerts" />
          )}
        </Section>
      )}

      {activeTab === "beneficiaries" && (
        <>
          <AdminPermission permission="users.beneficiaries.view">
            <Section title="Fiat Beneficiaries">
              {beneficiaries.length ? (
                beneficiaries.map((beneficiary: any, index: number) => (
                  <div
                    className="p-3 mb-3 border border-orange-100 rounded"
                    key={index}
                  >
                    <DataRow label="Name" value={beneficiary.name} />
                    <DataRow label="Bank" value={beneficiary.bank} />
                    <DataRow
                      label="Account"
                      value={beneficiary.accountNumber}
                    />
                  </div>
                ))
              ) : (
                <EmptyState label="No fiat beneficiaries" />
              )}
            </Section>
          </AdminPermission>
          <AdminPermission permission="users.savedAddresses.view">
            <Section title="Crypto Beneficiaries">
              {savedAddresses.length ? (
                savedAddresses.map((address: any, index: number) => (
                  <div
                    className="p-3 mb-3 border border-orange-100 rounded"
                    key={index}
                  >
                    <DataRow label="Label" value={address.label || "-"} />
                    <DataRow label="Network" value={address.network} />
                    <DataRow label="Address" value={address.walletAddress} />
                  </div>
                ))
              ) : (
                <EmptyState label="No crypto beneficiaries" />
              )}
            </Section>
          </AdminPermission>
        </>
      )}

      {activeTab === "statements" && (
        <Section title="Account Statement Requests">
          {statements.length ? (
            statements.map((statement: any) => (
              <div
                className="p-3 mb-3 border border-orange-100 rounded"
                key={statement.id}
              >
                <DataRow
                  label="Date requested"
                  value={dateLabel(statement.dateRequested)}
                />
                <DataRow
                  label="Wallet"
                  value={
                    Array.isArray(statement.walletTypes)
                      ? statement.walletTypes.join(", ")
                      : statement.walletTypes
                  }
                />
                <DataRow label="Period" value={statement.periodSelected} />
              </div>
            ))
          ) : (
            <EmptyState label="No statement requests" />
          )}
        </Section>
      )}

      {activeTab === "notes" && (
        <Section title="Internal Notes">
          <AdminPermission permission="users.internalNotes.create">
            <textarea
              className="w-full h-24 p-3 mb-3 text-sm border border-orange-100 rounded"
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add an internal note..."
              value={note}
            />
            <button
              className="px-4 py-2 mb-5 text-sm text-white bg-black rounded disabled:opacity-60"
              disabled={!note.trim() || appendNoteMutation.isLoading}
              onClick={() => appendNoteMutation.mutate(note.trim())}
              type="button"
            >
              Add Note
            </button>
          </AdminPermission>
          {internalNotes.length ? (
            internalNotes.map((internalNote: any) => (
              <div
                className="p-3 mb-3 border border-orange-100 rounded"
                key={internalNote.id}
              >
                <p className="mb-2 text-sm text-gray-800 whitespace-pre-wrap">
                  {internalNote.note}
                </p>
                <p className="text-xs text-gray-500">
                  {internalNote.admin?.displayName ||
                    `Admin ${internalNote.adminID}`}{" "}
                  · {dateLabel(internalNote.timestamp)}
                </p>
              </div>
            ))
          ) : (
            <EmptyState label="No internal notes" />
          )}
        </Section>
      )}
      <AdminPermission permission="users.security.view">
        <UserSecurity key={user.id} userId={Number(user.id)} />
      </AdminPermission>
      <AdminPermission permission="users.devices.view">
        <LinkedDevices userId={Number(user.id)} />
      </AdminPermission>
    </div>
  );
};

const LinkButton: React.FC<{ children: React.ReactNode; to: string }> = ({
  children,
  to,
}) => (
  <Link to={to}>
    <span className="inline-flex rounded-full bg-black px-4 py-1.5 text-sm text-white">
      {children}
    </span>
  </Link>
);

export default UserInfo;
